<template>
  <div class="auth-field">
    <div class="auth-field__label-row">
      <label class="auth-field__label" :for="fieldId">
        {{ label }}<span v-if="required" class="auth-field__required">*</span>
      </label>
      <slot name="label-action"></slot>
    </div>

    <div class="auth-field__control">
      <textarea
        v-if="type === 'textarea'"
        :id="fieldId"
        class="auth-field__input auth-field__input--textarea"
        :class="{ 'is-invalid': !!error }"
        :rows="rows"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="value"
        @input="$emit('input', $event.target.value)"
        @blur="$emit('blur')"></textarea>

      <input
        v-else
        :id="fieldId"
        class="auth-field__input"
        :class="{ 'is-invalid': !!error, 'auth-field__input--with-toggle': isPassword }"
        :type="resolvedType"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :value="value"
        @input="$emit('input', $event.target.value)"
        @blur="$emit('blur')"
        @keyup.enter="$emit('enter')" />

      <button
        v-if="isPassword"
        type="button"
        class="auth-field__toggle"
        :aria-label="isRevealed ? 'Hide password' : 'Show password'"
        @click="isRevealed = !isRevealed">
        <EyeOff v-if="isRevealed" :size="18" />
        <Eye v-else :size="18" />
      </button>
    </div>

    <p v-if="error" class="auth-field__error">{{ error }}</p>
    <p v-else-if="hint" class="auth-field__hint">{{ hint }}</p>
  </div>
</template>

<script>
  import { Eye, EyeOff } from 'lucide-vue';

  let fieldSequence = 0;

  export default {
    name: 'AuthTextField',
    components: {
      Eye,
      EyeOff
    },
    props: {
      value: {
        type: String,
        default: ''
      },
      label: {
        type: String,
        default: ''
      },
      type: {
        type: String,
        default: 'text'
      },
      placeholder: {
        type: String,
        default: ''
      },
      autocomplete: {
        type: String,
        default: 'off'
      },
      error: {
        type: String,
        default: ''
      },
      hint: {
        type: String,
        default: ''
      },
      required: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      rows: {
        type: Number,
        default: 2
      }
    },
    data() {
      fieldSequence += 1;
      return {
        fieldId: 'auth-field-' + fieldSequence,
        isRevealed: false
      }
    },
    computed: {
      isPassword() {
        return this.type === 'password';
      },
      resolvedType() {
        if (!this.isPassword) return this.type;
        return this.isRevealed ? 'text' : 'password';
      }
    }
  }
</script>

<style scoped>
.auth-field {
  margin-bottom: 16px;
}
.auth-field__label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.auth-field__label {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #1C2B36;
}
.auth-field__required {
  margin-left: 2px;
  color: #D92D20;
}
.auth-field__control {
  position: relative;
}
.auth-field__input {
  width: 100%;
  padding: 12px 14px;
  font-size: 0.9375rem;
  color: #1C2B36;
  background: #fff;
  border: 1px solid #E0E8EF;
  border-radius: 12px;
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.auth-field__input::placeholder {
  color: #A3AFBB;
}
.auth-field__input:focus {
  border-color: var(--merchant-primary-color, #1AA179);
  box-shadow: 0 0 0 3px var(--merchant-primary-color-alpha-15, rgba(26, 161, 121, 0.15));
}
.auth-field__input:disabled {
  background: #F3F6F9;
  cursor: not-allowed;
}
.auth-field__input--with-toggle {
  padding-right: 46px;
}
.auth-field__input--textarea {
  resize: vertical;
}
.auth-field__input.is-invalid {
  border-color: #D92D20;
}
.auth-field__toggle {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #6B7886;
  background: transparent;
  border: none;
  cursor: pointer;
}
.auth-field__error {
  margin: 6px 0 0;
  font-size: 0.75rem;
  color: #D92D20;
}
.auth-field__hint {
  margin: 6px 0 0;
  font-size: 0.75rem;
  color: #6B7886;
}
</style>



// WEBPACK FOOTER //
// src/components/v2/auth/AuthTextField.vue